import { ChangeEvent, useEffect, useState } from 'react';
import InputField from '../field/input/InputField';
import SliderField from '../field/slider/SliderField';
import './inputWithSlider.scss';
import { BaseFieldProps, FieldState } from '../field/Field';

interface InputSliderFieldProps extends BaseFieldProps {
  id: string;
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  unit?: string;
  unitPosition?: 'left' | 'right';
  tagline?: string;
}

const InputSliderField = ({
  id,
  value = 0,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  placeholder,
  unit,
  unitPosition,
  tagline,
  ...props
}: InputSliderFieldProps) => {
  const [committedValue, setCommittedValue] = useState<number>(value);
  const [inputValue, setInputValue] = useState<string>(value.toString());
  const [state, setState] = useState<FieldState | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    setCommittedValue(value);
    setInputValue(value.toString());
  }, [value]);

  const validateValue = (val: number) => {
    if (val < min || val > max) {
      setState(FieldState.INVALID);
      setErrorMessage(`Value must be between ${min} and ${max}`);
      return false;
    } else {
      setState(undefined);
      setErrorMessage(undefined);
      return true;
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const parsed = parseFloat(inputValue);
      if (!isNaN(parsed) && validateValue(parsed)) {
        setCommittedValue(parsed);
        if (onChange) onChange(parsed);
      }
    }
  };

  const handleSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (!isNaN(val)) {
      setCommittedValue(val);
      setInputValue(val.toString());
      validateValue(val);
    }
  };

  const handleSliderMouseUp = () => {
    if (onChange) {
      onChange(committedValue);
    }
  };

  return (
    <div className="input-slider-field a-form-field">
      <InputField
        id={`${id}-input`}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        unit={unit}
        unitPosition={unitPosition}
        state={state}
        errorMessage={errorMessage}
        {...props}
      />
      <SliderField
        id={`${id}-slider`}
        value={committedValue}
        onChange={handleSliderChange}
        onMouseUp={handleSliderMouseUp}
        min={min}
        max={max}
        step={step}
        tagline={tagline}
      />
    </div>
  );
};

export default InputSliderField;
